import { SessionStorageStore, SessionStorageContext } from 'lib/context/app'
import { useContext, useState, useEffect, useCallback } from 'react'

/** Functional updater or direct value, similar to useState */
type SetStateAction<T> = T | ((prev: T) => T)

/**
 * Similar to useSessionStorage, but creates a global context for the store, so that the value
 * can be synced across multiple components.
 *
 * @param key - The key to store the value under
 * @param defaultValue - The default value to use if the key is not found in the context store or localStorage
 * @returns A tuple containing the current value and a function to update it (supports functional updates)
 */
export function useSessionStorageContext<K extends keyof SessionStorageStore>(
    key: K,
    defaultValue: SessionStorageStore[K],
): [SessionStorageStore[K], (value: SetStateAction<SessionStorageStore[K]>) => void] {
    const { store, setStore } = useContext(SessionStorageContext)

    const [value, setValue] = useState<SessionStorageStore[K]>(() => {
        // If the context store already has a value for this key:
        if (store[key] !== undefined) {
            return store[key]
        }

        // Otherwise, look in localStorage
        const lsItem = sessionStorage.getItem(key)
        if (lsItem !== null) {
            return JSON.parse(lsItem)
        }

        // Otherwise, set it to the provided default value
        return defaultValue
    })

    useEffect(() => {
        if (store[key] !== undefined) {
            setValue(store[key])
        }
    }, [store, key, defaultValue])

    const setSyncedValue = useCallback(
        (action: SetStateAction<SessionStorageStore[K]>) => {
            // Use setValue's functional form to get the latest value and avoid stale closures
            setValue((currentValue) => {
                const newValue = typeof action === 'function'
                    ? (action as (prev: SessionStorageStore[K]) => SessionStorageStore[K])(currentValue)
                    : action

                // Update SessionStorage
                sessionStorage.setItem(key, JSON.stringify(newValue))

                // Update this item's value in the context store
                setStore((prev) => ({ ...prev, [key]: newValue }))

                return newValue
            })
        },
        [key, setStore],
    )

    return [value, setSyncedValue]
}
